"use client";
import React, { useRef, useState } from "react";
import styles from "./Listbox.module.scss";
import Link from "next/link";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import { ClickOutside } from "@aleksundshantu/aunds-utils-react/accessibility";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG.client";

interface ListProps {
  items: { label: string; value: string }[];
  selected: string;
  setSelected: (value: string) => void;
  disableDropdown?: boolean;
  availableTranslations: { lang: string; path: string }[];
  alwaysOpenUntil?: "navbar-breakpoint";
  closeOnSelection?: boolean;
}

export const List = ({
  items,
  selected: _selected,
  setSelected: _setSelected,
  disableDropdown,
  availableTranslations,
  alwaysOpenUntil,
  closeOnSelection,
}: ListProps) => {
  //state that is only used internally if no external state is provided
  const [internalSelected, setInternalSelected] = useState<string | undefined>(
    undefined,
  );
  const selected = _selected ?? internalSelected;
  const setSelected = _setSelected ?? setInternalSelected;

  const [open, setOpen] = useState(false);

  const ref = useRef<HTMLUListElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  const { label, getIcon } = useGlobalContext();

  return (
    <div
      data-is-open={open}
      className={`${styles.listbox} dropdown ${open ? "is-active" : ""} ${alwaysOpenUntil ? styles[`always-open-until-${alwaysOpenUntil}`] : ""}`}
    >
      <div className={`dropdown-trigger ${disableDropdown ? "disabled" : ""}`}>
        <button
          className="button is-flex is-align-items-center is-gap-1"
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          onClick={() => {
            setOpen(!open);
          }}
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              setOpen(!open);
            }
            if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          ref={button}
        >
          <span className="navbar-link selected-lang">
            {selected
              ? items.find((i) => i.value === selected)?.label
              : "Auswählen..."}
          </span>
          {!disableDropdown && (
            <div className={styles["arrow-wrapper"]}>
              <CustomSVG
                src={getIcon("chevron-down")}
                wrapper={"span"}
                width={12}
                height={12}
                alt={label(
                  "global",
                  null,
                  "label_chevron_down",
                  "Chevron nach unten",
                )}
                className="is-flex"
              />
            </div>
          )}
        </button>
      </div>
      {!disableDropdown && (
        <div className="dropdown-menu" id="dropdown-menu">
          <ClickOutside
            elementToExclude={button}
            onClickOutside={() => setOpen(false)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- ARIA APG: focus must move into dropdown on open */}
            <FocusTrap dep={open} disabled={!open} autoFocus>
              <ul
                role="menu"
                className="dropdown-content m-0"
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    ref.current?.focus();
                  }
                }}
                aria-label={label("global", "aria_label", "language_toggle", {
                  en: "Language menu",
                  de: "Sprachmenü",
                })}
              >
                {items.map((item, index) => {
                  const translatedPath = availableTranslations.find(
                    (x) => x.lang === item.value,
                  );

                  return (
                    <li className="dropdown-item m-0" key={index} role="none">
                      {
                        // eslint-disable-next-line react/forbid-elements
                        <Link
                          role="menuitem"
                          href={translatedPath?.path ?? ""}
                          locale={item.value}
                          aria-label={
                            label(
                              "global",
                              "aria_label",
                              "language_toggle_option_" + item.value,
                              "",
                            ) || undefined
                          }
                          className={` navbar-link ${selected === item.value ? "is-active" : ""}`}
                          onClick={() => {
                            if (closeOnSelection) setOpen(false);
                            setSelected(item.value);
                          }}
                          lang={item.value}
                          hrefLang={item.value}
                        >
                          {item.label}
                        </Link>
                      }
                    </li>
                  );
                })}
              </ul>
            </FocusTrap>
          </ClickOutside>
        </div>
      )}
    </div>
  );
};
/* jscpd:ignore-start */
export type ListPropsV2 = {
  buttonId?: string;
  items: { label: string; value: string }[];
  selected?: string;
  setSelected: (value: string) => void;
  disableDropdown?: boolean;
  availableTranslations?: { lang: string; path: string }[];
  alwaysOpenUntil?: "navbar-breakpoint";
  className?: string;
  placeholder?: string;
  "aria-describedby"?: string;
};

export const ListV2 = ({
  items,
  selected: _selected,
  setSelected: _setSelected,
  disableDropdown,
  availableTranslations,
  alwaysOpenUntil,
  buttonId,
  className = "",
  placeholder = "Auswählen...",
  "aria-describedby": ariaDescribedby,
}: ListPropsV2) => {
  //state that is only used internally if no external state is provided
  const [internalSelected, setInternalSelected] = useState<string | undefined>(
    undefined,
  );
  const selected = _selected ?? internalSelected;
  const setSelected = _setSelected ?? setInternalSelected;

  const [open, setOpen] = useState(false);

  const ref = useRef<HTMLUListElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const { getIcon } = useGlobalContext();

  return (
    <div
      data-is-open={open}
      className={`${styles["listbox"]} ${className} dropdown ${open ? "is-active" : ""} ${
        alwaysOpenUntil ? styles[`always-open-until-${alwaysOpenUntil}`] : ""
      }`}
    >
      <div
        className={`dropdown-trigger w-100 ${
          disableDropdown ? "disabled" : ""
        }`}
      >
        <button
          type={"button"} // important! otherwise forms with listbox inside will be submitted
          id={buttonId}
          className="button is-flex is-align-items-center is-justify-content-space-between is-gap-1 has-text-left w-100"
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          aria-describedby={ariaDescribedby}
          onClick={() => {
            setOpen(!open);
          }}
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              setOpen(!open);
            }
            if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          ref={button}
        >
          <span
            className={
              availableTranslations
                ? "navbar-link selected-lang"
                : `placeholder ${selected ? "is-selected" : ""}`
            }
          >
            {selected
              ? items.find((i) => i.value === selected)?.label
              : placeholder}
          </span>
          {!disableDropdown && (
            <div className={styles["arrow-wrapper"]}>
              <CustomSVG
                src={getIcon("chevron-down")}
                wrapper={"span"}
                width={12}
                height={12}
                alt="chevron-down"
                className="is-flex"
              />
            </div>
          )}
        </button>
      </div>
      {!disableDropdown && (
        <div
          className="dropdown-menu w-100"
          id="dropdown-menu"
          data-lenis-prevent
        >
          <ClickOutside
            elementToExclude={button}
            onClickOutside={() => setOpen(false)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- ARIA APG: focus must move into listbox on open */}
            <FocusTrap dep={open} disabled={!open} autoFocus>
              <ul
                role="listbox"
                className="dropdown-content m-0"
                onKeyUp={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    ref.current?.focus();
                  }
                }}
              >
                {items.map((item, index) => {
                  const translatedPath = availableTranslations?.find(
                    (x) => x.lang === item.value,
                  );

                  if (translatedPath) {
                    return (
                      <li className="dropdown-item m-0" key={index}>
                        {
                          // eslint-disable-next-line react/forbid-elements
                          <Link
                            role="menuitem"
                            href={translatedPath?.path ?? ""}
                            locale={item.value}
                            className={` navbar-link ${
                              selected === item.value ? "is-active" : ""
                            }`}
                            onClick={() => setSelected(item.value)}
                            lang={item.value}
                            hrefLang={item.value}
                          >
                            {item.label}
                          </Link>
                        }
                      </li>
                    );
                  }

                  return (
                    <li
                      className="dropdown-item m-0"
                      key={index}
                      role="option"
                      aria-selected={selected === item.value}
                      onKeyDown={(
                        event: React.KeyboardEvent<HTMLLIElement>,
                      ) => {
                        if (event.key === "Enter") {
                          setOpen(false);
                          setSelected(item.value);
                        }
                      }}
                      onClick={() => {
                        setOpen(false);
                        setSelected(item.value);
                      }}
                    >
                      {item.label}
                    </li>
                  );
                })}
              </ul>
            </FocusTrap>
          </ClickOutside>
        </div>
      )}
    </div>
  );
};
/* jscpd:ignore-end */
